import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON, {
  auth: {
    detectSessionInUrl: true,
    persistSession: true,
    autoRefreshToken: true,
    flowType: 'pkce',
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Session management — anonymous, persisted in localStorage
// ─────────────────────────────────────────────────────────────────────────────

function generateSessionId() {
  return "ses_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export async function getOrCreateSession() {
  let sessionId = localStorage.getItem("hh_session_id");

  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem("hh_session_id", sessionId);
    await supabase.from("sessions").insert({ id: sessionId });
  } else {
    await supabase.from("sessions").update({ last_seen_at: new Date().toISOString() }).eq("id", sessionId);
  }

  return sessionId;
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth — Google OAuth
// ─────────────────────────────────────────────────────────────────────────────

export async function signInWithMagicLink(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin + '/' },
  });
  if (error) throw error;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user || null;
}

export function onAuthStateChange(callback) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    function(event, session) { callback(event, session?.user || null); }
  );
  return subscription;
}

export async function mergeSessionToUser(sessionId, userId) {
  if (!sessionId || !userId) return;
  const { error } = await supabase.rpc("merge_session_to_user", {
    p_session_id: sessionId,
    p_user_id:    userId,
  });
  if (error) console.error("mergeSessionToUser:", error);
}

// ─────────────────────────────────────────────────────────────────────────────
// Save a design
// ─────────────────────────────────────────────────────────────────────────────

export async function saveDesign({
  sessionId, userId, dbId, form, measurements, consentGiven,
  designRender, designRenderBack, fittingRoomRender, fittingRoomRenderBack,
  designSeed, designBrief, fabricYardage, costEstimate, fittingRoomAdjustments, status,
}) {
  const row = {
    session_id:               sessionId,
    user_id:                  userId || null,
    category:                 form.category             || null,
    color_description:        form.colorDescription     || null,
    color_hex:                form.colorHex             || null,
    fabric_id:                form.fabricId             || null,
    custom_fabric:            form.customFabric         || null,
    lining:                   form.lining               || null,
    cut:                      form.cut                  || null,
    neckline:                 form.neckline             || null,
    sleeves:                  form.sleeves              || null,
    dress_length:             form.dressLength          || null,
    separates:                !!form.separates,
    skirt_type:               form.skirtType            || null,
    skirt_length:             form.skirtLength          || null,
    atmosphere:               form.atmosphere           || null,
    original_comments:        form.originalComments     || null,
    ai_interpreted_comments:  form.aiInterpretedComments|| null,
    design_details:           form.designDetails        || null,
    design_details_summary:   form.designDetailsSummary || null,
    design_render:            designRender              || null,
    design_render_back:       designRenderBack          || null,
    fitting_room_render:      fittingRoomRender         || null,
    fitting_room_render_back: fittingRoomRenderBack     || null,
    design_seed:              designSeed                || null,
    design_brief:             designBrief               || null,
    fabric_yardage:           fabricYardage             || null,
    cost_estimate:            costEstimate              || null,
    fitting_room_adjustments: fittingRoomAdjustments   || [],
    status:                   status                    || "Design",
    consent_given:            !!consentGiven,
    measurements:             consentGiven ? measurements : null,
  };

  if (dbId) {
    const { data, error } = await supabase.from("designs").update(row).eq("id", dbId).select("id").single();
    if (error) throw error;
    return data.id;
  }

  const { data, error } = await supabase.from("designs").insert(row).select("id").single();
  if (error) throw error;
  return data.id;
}

// ─────────────────────────────────────────────────────────────────────────────
// Load all designs for a session
// ─────────────────────────────────────────────────────────────────────────────

export async function loadSessionDesigns(sessionId, userId) {
  let query = supabase
    .from("designs")
    .select("*")
    .order("created_at", { ascending: false });

  if (userId) {
    query = query.eq("user_id", userId);
  } else {
    query = query.eq("session_id", sessionId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(dbRowToCollectionItem);
}

// ─────────────────────────────────────────────────────────────────────────────
// Look up a single design by seed number
// ─────────────────────────────────────────────────────────────────────────────

export async function loadDesignBySeed(seed) {
  const { data, error } = await supabase
    .from("designs")
    .select("*")
    .eq("design_seed", Number(seed))
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return dbRowToCollectionItem(data);
}

// ─────────────────────────────────────────────────────────────────────────────
// Delete a design
// ─────────────────────────────────────────────────────────────────────────────

export async function deleteDesign(designId) {
  const { error } = await supabase.from("designs").delete().eq("id", designId);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Share token
// ─────────────────────────────────────────────────────────────────────────────

export async function createShareToken(designId) {
  const { data, error } = await supabase
    .from("share_tokens")
    .insert({ design_id: designId })
    .select("token")
    .single();
  if (error) throw error;
  return data.token;
}

export async function loadSharedDesign(token) {
  const { data, error } = await supabase
    .from("share_tokens")
    .select("design_id, designs(*)")
    .eq("token", token)
    .single();
  if (error) throw error;
  if (!data?.designs) return null;
  return dbRowToCollectionItem(data.designs);
}

// ─────────────────────────────────────────────────────────────────────────────
// Gallery — load all is_gallery designs from the designs table (no auth required)
// ─────────────────────────────────────────────────────────────────────────────

export async function loadGalleryDesigns() {
  const { data, error } = await supabase
    .from("designs")
    .select("id, created_at, status, design_seed, design_render, category, color_description, color_hex, fabric_id, custom_fabric, lining, cut, neckline, sleeves, dress_length, separates, skirt_type, skirt_length, atmosphere, original_comments, ai_interpreted_comments, design_details, design_details_summary")
    .eq("is_gallery", true)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(dbRowToCollectionItem);
}

// ─────────────────────────────────────────────────────────────────────────────
// User — submit a saved design for gallery consideration (sets pending_gallery)
// ─────────────────────────────────────────────────────────────────────────────

export async function submitDesignToGallery(designId) {
  const { error } = await supabase
    .from("designs")
    .update({ gallery_status: "pending" })
    .eq("id", designId);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin — load all designs pending gallery approval
// ─────────────────────────────────────────────────────────────────────────────

export async function loadPendingGalleryDesigns() {
  const { data, error } = await supabase
    .from("designs")
    .select("id, created_at, status, design_seed, design_render, category, color_description, color_hex, fabric_id, custom_fabric, lining, cut, neckline, sleeves, dress_length, separates, skirt_type, skirt_length, atmosphere, original_comments, ai_interpreted_comments, design_details, design_details_summary, gallery_status")
    .eq("gallery_status", "pending")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(dbRowToCollectionItem);
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin — approve a pending design (uploads render to Storage, sets is_gallery)
// ─────────────────────────────────────────────────────────────────────────────

export async function approveGalleryDesign(designId, designRender, designSeed) {
  // Upload render to permanent Storage if it's still a base64 blob
  const renderUrl = await uploadRenderToStorage(designRender, designSeed);
  const { error } = await supabase
    .from("designs")
    .update({ is_gallery: true, gallery_status: "approved", design_render: renderUrl })
    .eq("id", designId);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin — reject a pending design
// ─────────────────────────────────────────────────────────────────────────────

export async function rejectGalleryDesign(designId) {
  const { error } = await supabase
    .from("designs")
    .update({ gallery_status: "rejected" })
    .eq("id", designId);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin — save a design to the gallery (uploads image to Storage, saves URL)
// ─────────────────────────────────────────────────────────────────────────────

async function uploadRenderToStorage(base64OrUrl, designSeed) {
  // If it's already a Storage URL, return it as-is
  if (!base64OrUrl || base64OrUrl.startsWith("http")) return base64OrUrl;

  // Convert base64 to blob
  const base64Data = base64OrUrl.includes(",") ? base64OrUrl.split(",")[1] : base64OrUrl;
  const mimeType   = base64OrUrl.includes("data:") ? base64OrUrl.split(";")[0].split(":")[1] : "image/png";
  const byteChars  = atob(base64Data);
  const byteArr    = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArr], { type: mimeType });

  // Upload to Storage
  const filename = `gallery/${designSeed || Date.now()}.png`;
  const { error: uploadError } = await supabase.storage
    .from("design-renders")
    .upload(filename, blob, { contentType: mimeType, upsert: true });
  if (uploadError) throw uploadError;

  // Return the public URL
  const { data } = supabase.storage
    .from("design-renders")
    .getPublicUrl(filename);
  return data.publicUrl;
}

export async function savePublicDesign({ form, designRender, designSeed, userId }) {
  // Upload image to Storage first — saves URL not base64
  const renderUrl = await uploadRenderToStorage(designRender, designSeed);

  // Include both identifiers so the RLS policy can match on either
  const sessionId = localStorage.getItem("hh_session_id") || null;

  const row = {
    user_id:                  userId                    || null,
    session_id:               sessionId,
    category:                 form.category             || null,
    color_description:        form.colorDescription     || null,
    color_hex:                form.colorHex             || null,
    fabric_id:                form.fabricId             || null,
    custom_fabric:            form.customFabric         || null,
    lining:                   form.lining               || null,
    cut:                      form.cut                  || null,
    neckline:                 form.neckline             || null,
    sleeves:                  form.sleeves              || null,
    dress_length:             form.dressLength          || null,
    separates:                !!form.separates,
    skirt_type:               form.skirtType            || null,
    skirt_length:             form.skirtLength          || null,
    atmosphere:               form.atmosphere           || null,
    original_comments:        form.originalComments     || null,
    ai_interpreted_comments:  form.aiInterpretedComments|| null,
    design_details:           form.designDetails        || null,
    design_details_summary:   form.designDetailsSummary || null,
    design_render:            renderUrl                 || null,
    design_seed:              designSeed                || null,
    status:                   "Design",
    is_gallery:               true,
  };
  const { error } = await supabase.from("designs").insert(row);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// Convert a DB row back into the shape the app expects
// ─────────────────────────────────────────────────────────────────────────────

function dbRowToCollectionItem(row) {
  return {
    id:                      row.id,
    dbId:                    row.id,
    savedAt:                 new Date(row.created_at).toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" }),
    status:                  row.status || "Design",
    consentGiven:            !!row.consent_given,
    designSeed:              row.design_seed || null,
    modelRender:             row.design_render || null,
    modelRenderBack:         row.design_render_back || null,
    designRender:            row.design_render || null,
    designRenderBack:        row.design_render_back || null,
    fittingRoomRender:       row.fitting_room_render || null,
    fittingRoomRenderBack:   row.fitting_room_render_back || null,
    designBrief:             row.design_brief || "",
    fabricYardage:           row.fabric_yardage || null,
    costEstimate:            row.cost_estimate || null,
    fittingRoomAdjustments:  row.fitting_room_adjustments || [],
    measurements:            row.measurements || {},
    form: {
      category:              row.category              || "cocktail",
      colorDescription:      row.color_description     || "Dusty Rose",
      colorHex:              row.color_hex             || "#c9a0a0",
      fabricId:              row.fabric_id             || "silk",
      customFabric:          row.custom_fabric         || "",
      lining:                row.lining                || "Fully Lined",
      cut:                   row.cut                   || "A-line",
      customCut:             row.custom_cut            || "",
      neckline:              row.neckline              || "V-Neck",
      customNeckline:        row.custom_neckline       || "",
      backNeckline:          row.back_neckline         || "",
      customBackNeckline:    row.custom_back_neckline  || "",
      sleeves:               row.sleeves               || "Sleeveless",
      customSleeves:         row.custom_sleeves        || "",
      closure:               row.closure               || "",
      customClosure:         row.custom_closure        || "",
      dressLength:           row.dress_length          || "Midi",
      customDressLength:     row.custom_dress_length   || "",
      separates:             !!row.separates,
      skirtType:             row.skirt_type            || "A-line",
      customSkirtType:       row.custom_skirt_type     || "",
      skirtLength:           row.skirt_length          || "Midi",
      customSkirtLength:     row.custom_skirt_length   || "",
      jacketNeckline:        row.jacket_neckline       || "",
      customJacketNeckline:  row.custom_jacket_neckline|| "",
      jacketSleeves:         row.jacket_sleeves        || "",
      customJacketSleeves:   row.custom_jacket_sleeves || "",
      jacketFront:           row.jacket_front          || "",
      jacketBack:            row.jacket_back           || "",
      skirtFront:            row.skirt_front           || "",
      skirtBack:             row.skirt_back            || "",
      frontDescription:      row.front_description     || "",
      backDescription:       row.back_description      || "",
      atmosphere:            row.atmosphere            || "",
      originalComments:      row.original_comments     || "",
      aiInterpretedComments: row.ai_interpreted_comments || "",
      designDetails:         row.design_details        || null,
      designDetailsSummary:  row.design_details_summary || "",
    },
  };
}
